import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Footer from '@/components/Footer';
import ImageViewer from '@/components/ImageViewer';
import { useState } from 'react';

const projectsData = [
  {
    id: "report-scribe-ehr",
    title: "ReportScribe EHR",
    description: "An intelligent document automation platform that generates structured medical reports in PDF format from user inputs and uploaded documents.",
    image: "/images/report-scribe-ehr/1.png",
    tags: ["Healthcare", "Document Automation", "PDF Processing"],
    problemStatement: "Healthcare professionals often spend a significant amount of time manually creating and formatting patient reports. This manual process increases administrative workload and introduces potential for human error in documentation.",
    solution: "We developed ReportScribe EHR, an automated system that processes structured and unstructured data inputs to generate professional, standardized medical reports in PDF format. It supports PDF parsing, field extraction, and template-based report creation to streamline medical documentation.",
    screenshots: [
      "/images/report-scribe-ehr/2.png",
      "/images/report-scribe-ehr/3.png",
      "/images/report-scribe-ehr/4.png"
    ],
    businessValue: [
      "Reduced report creation time by over 80%",
      "Improved accuracy and consistency in medical documentation",
      "Minimized administrative workload for healthcare professionals",
      "Enabled faster turnaround for patient report delivery",
      "Standardized PDF format ensures interoperability and compliance"
    ],
    timeline: "6 months"
  },
  {
    id: "med-flow-emr",
    title: "MedFlow EMR",
    description: "A hospital management solution that automates meeting documentation and integrates appointment scheduling to reduce administrative workload for medical staff.",
    image: "/images/med-flow-emr/1.png",
    tags: ["Healthcare", "EMR", "Automation", "Speech-to-Text", "Scheduling"],
    problemStatement: "Doctors and healthcare staff often spend valuable time manually taking notes during meetings or patient consultations and later entering that information into electronic records. This repetitive process increases administrative overhead and reduces time available for patient care.",
    solution: "We developed MedFlow EMR, a system that automatically records and transcribes meetings, saving structured information directly into the hospital database. It also includes an integrated appointment management module that simplifies scheduling and coordination between patients and doctors.",
    screenshots: [
      "/images/med-flow-emr/2.png",
      "/images/med-flow-emr/3.png",
      "/images/med-flow-emr/4.png"
    ],
    businessValue: [
      "Eliminated the need for manual note-taking during meetings",
      "Reduced administrative time by over 70%",
      "Improved data accuracy and record completeness",
      "Enabled seamless appointment scheduling and tracking",
      "Increased overall staff efficiency and patient satisfaction"
    ],
    timeline: "12 months"
  },
  {
    id: "resume-ai",
    title: "Resume AI",
    description: "An AI-driven platform that screens resumes using explainable algorithms and conducts simulated interviews through a conversational interface.",
    image: "/images/resume-ai/1.png",
    tags: ["Recruitment Automation", "Efficiency", "Ethical AI"],
    problemStatement: "Recruiters often spend significant time manually reviewing resumes and conducting repetitive initial interviews, which leads to delays in hiring and potential bias in candidate evaluation.",
    solution: "We built ResumeAI, a transparent and ethically designed system that automatically analyzes and classifies resumes using explainable AI models. The platform also features an interactive interview bot that simulates initial candidate screenings, providing structured feedback and reducing recruiter workload.",
    screenshots: [
      "/images/resume-ai/2.png",
      "/images/resume-ai/3.png",
      "/images/resume-ai/4.png"
    ],
    businessValue: [
      "Reduced candidate screening time by 70%",
      "Improved fairness and transparency in hiring decisions",
      "Automated initial interview stages with conversational AI",
      "Enabled recruiters to focus on high-value candidate interactions",
      "Delivered explainable insights for compliance and ethical assurance"
    ],
    timeline: "3 months"
  },
  {
    id: "hotel-inventory-ai",
    title: "Hotel Inventory AI",
    description: "An AI-powered inventory management platform that helps hotels forecast demand, optimize stock levels, and reduce waste through data-driven insights.",
    image: "/images/hotel-inventory-ai/1.png",
    tags: ["Hospitality", "Operational Efficiency", "Predictive Analytics", "Cost Optimization"],
    problemStatement: "Hotels faced challenges managing inventory efficiently due to fluctuating occupancy rates, manual tracking, and limited visibility into usage patterns. This led to overstocking, waste, and increased operational costs.",
    solution: "We developed HotelInventoryAI, a smart system that leverages historical usage data, occupancy forecasts, and item lifespans to predict future inventory needs. The platform automatically suggests restocking levels, helping hotels reduce waste and maintain optimal inventory levels year-round.",
    screenshots: [
      "/images/hotel-inventory-ai/2.png",
      "/images/hotel-inventory-ai/3.png",
      "/images/hotel-inventory-ai/4.png"
    ],
    businessValue: [
      "Reduced inventory waste by 40%",
      "Optimized stock levels to match seasonal demand",
      "Lowered procurement costs by 25%",
      "Improved resource planning efficiency by 60%",
      "Enhanced decision-making with AI-driven forecasting"
    ],
    timeline: "6 months"
  },
  {
    id: "warehouse-support",
    title: "Warehouse Support",
    description: "An AI-powered internal assistant that helps warehouse employees instantly access procedural, operational, and HR information through natural conversation.",
    image: "/images/warehouse-support/1.png",
    tags: ["Operational Efficiency", "Employee Support", "Knowledge Management", "AI Assistance"],
    problemStatement: "Warehouse employees often struggled to find quick answers to operational or HR-related questions, leading to workflow interruptions, dependency on supervisors, and reduced overall productivity.",
    solution: "We developed WarehouseSupport, an AI-powered assistant trained on company documentation and internal policies. The tool enables employees to ask questions in natural language—covering operations, troubleshooting, and HR processes—and get instant, accurate answers tailored to their context.",
    screenshots: [
      "/images/warehouse-support/2.png",
      "/images/warehouse-support/3.png",
      "/images/warehouse-support/4.png"
    ],
    businessValue: [
      "Reduced supervisor interruptions by 50%",
      "Improved employee productivity by 35%",
      "Enabled 24/7 access to internal knowledge and procedures",
      "Accelerated onboarding and training processes",
      "Enhanced employee satisfaction and operational consistency"
    ],
    timeline: "4 months"
  },
  {
    id: "feedback-ai",
    title: "Feedback AI",
    description: "An AI-driven web application that collects and analyzes customer feedback from multiple channels to uncover actionable insights and improve satisfaction.",
    image: "/images/feedback-ai/1.png",
    tags: ["Customer Experience", "Sentiment Analysis", "Business Intelligence", "Hospitality & Retail"],
    problemStatement: "Retail and hospitality businesses received feedback from multiple channels—surveys, reviews, emails, and social media—but lacked an integrated system to analyze sentiment and extract meaningful insights, limiting their ability to act on customer needs effectively.",
    solution: "We created FeedbackAI, an AI-powered platform that aggregates feedback from diverse sources, analyzes customer sentiment using natural language processing, and visualizes results in an intuitive dashboard. The system helps managers identify key pain points, track satisfaction trends, and prioritize improvements in real time.",
    screenshots: [
      "/images/feedback-ai/2.png",
      "/images/feedback-ai/3.png",
      "/images/feedback-ai/4.png"
    ],
    businessValue: [
      "Increased customer satisfaction scores by 40%",
      "Enabled data-driven service improvements across locations",
      "Reduced manual review time by 70%",
      "Unified customer insights from 5+ feedback channels",
      "Enhanced brand reputation and loyalty tracking"
    ],
    timeline: "6 months"
  },
  {
    id: "med-insight",
    title: "Med Insight",
    description: "An AI-powered tool that analyzes medical documents, extracts key findings, and generates concise summaries, alerts, and patient-friendly explanations for improved healthcare communication.",
    image: "/images/med-insight/1.png",
    tags: ["Healthcare Innovation", "Operational Efficiency", "AI Insights", "Patient Communication"],
    problemStatement: "Doctors and medical staff spend a significant amount of time manually reviewing lab results, medical reports, and handwritten notes to extract critical information. This process is time-consuming, error-prone, and delays patient communication.",
    solution: "We developed MedInsight, an AI-powered system that automatically processes medical reports, lab results, and doctor notes to identify key findings and highlight abnormalities. It generates concise summaries for medical professionals, produces clear explanations for patients, and stores structured insights for historical tracking and analysis.",
    screenshots: [
      "/images/med-insight/2.png",
      "/images/med-insight/3.png",
      "/images/med-insight/4.png"
    ],
    businessValue: [
      "Reduced report review time by 65%",
      "Improved diagnostic accuracy and consistency",
      "Enabled faster patient communication with automated summaries",
      "Decreased administrative workload for doctors and nurses",
      "Created structured data for long-term clinical insights"
    ],
    timeline: "7 months"
  },
  {
    id: "finance-flow",
    title: "Finance Flow",
    description: "An AI-powered financial management tool that automates expense tracking, cash flow forecasting, and risk detection to help small and medium-sized businesses make smarter financial decisions.",
    image: "/images/finance-flow/1.png",
    tags: ["Financial Management", "SMEs", "Predictive Analytics", "Cost Optimization"],
    problemStatement: "Small and medium-sized enterprises often lack dedicated finance teams and struggle to maintain visibility over their expenses, cash flow, and potential financial risks. Manual tracking leads to inefficiencies, errors, and missed opportunities for cost savings.",
    solution: "We built FinanceFlow, an intelligent platform that automatically categorizes company transactions, monitors cash flow in real time, and forecasts short-term liquidity. The AI identifies unusual spending patterns, suggests budget optimizations, and generates detailed reports and dashboards to support proactive financial management.",
    screenshots: [
      "/images/finance-flow/2.png",
      "/images/finance-flow/3.png",
      "/images/finance-flow/4.png"
    ],
    businessValue: [
      "Automated 90% of expense tracking tasks",
      "Improved cash flow forecasting accuracy by 80%",
      "Reduced financial reporting time by 60%",
      "Detected irregular spending patterns early, minimizing risk",
      "Enabled informed decision-making for non-financial managers"
    ],
    timeline: "6 months"
  }
];

const ProjectDetails = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
  
  const project = projectsData.find(p => p.id === projectId);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Project Not Found</h1>
          <Button onClick={() => navigate('/portfolio')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Portfolio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-20 px-6 md:px-10 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/portfolio')}
            className="mb-8"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Portfolio
          </Button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{project.title}</h1>
              <p className="text-lg text-muted-foreground mb-6">{project.description}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {project.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {/*<div className="grid grid-cols-2 gap-4 text-sm">*/}
              {/*  <div>*/}
              {/*    <p className="text-muted-foreground">Timeline</p>*/}
              {/*    <p className="font-semibold">{project.timeline}</p>*/}
              {/*  </div>*/}
              {/*</div>*/}
            </div>
            <div 
              className="aspect-video rounded-xl overflow-hidden shadow-lg cursor-zoom-in"
              onClick={() => setSelectedImage({ src: project.image, alt: project.title })}
            >
              <img 
                src={project.image} 
                alt={project.title}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 30%' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 px-6 md:px-10 lg:px-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>The Challenge</CardTitle>
              <CardDescription>Understanding the problem we set out to solve</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-foreground leading-relaxed">{project.problemStatement}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16 px-6 md:px-10 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Our Solution</CardTitle>
              <CardDescription>How we addressed the challenge</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-foreground leading-relaxed">{project.solution}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Screenshots */}
      <section className="py-16 px-6 md:px-10 lg:px-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8">Project Showcase</h2>
          <div className="grid md:grid-cols-1 gap-8">
            {project.screenshots.map((screenshot, index) => (
              <div 
                key={index}
                className="aspect-video rounded-lg overflow-hidden shadow-xl hover:shadow-2xl transition-shadow cursor-zoom-in"
                onClick={() => setSelectedImage({ src: screenshot, alt: `${project.title} screenshot ${index + 1}` })}
              >
                <img 
                  src={screenshot} 
                  alt={`${project.title} screenshot ${index + 1}`}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 10%' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Value */}
      {/*<section className="py-16 px-6 md:px-10 lg:px-20">*/}
      {/*  <div className="max-w-7xl mx-auto">*/}
      {/*    <Card>*/}
      {/*      <CardHeader>*/}
      {/*        <CardTitle>Business Impact</CardTitle>*/}
      {/*        <CardDescription>Measurable results and value delivered</CardDescription>*/}
      {/*      </CardHeader>*/}
      {/*      <CardContent>*/}
      {/*        <ul className="space-y-4">*/}
      {/*          {project.businessValue.map((value, index) => (*/}
      {/*            <li key={index} className="flex items-start gap-3">*/}
      {/*              <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />*/}
      {/*              <span className="text-foreground">{value}</span>*/}
      {/*            </li>*/}
      {/*          ))}*/}
      {/*        </ul>*/}
      {/*      </CardContent>*/}
      {/*    </Card>*/}
      {/*  </div>*/}
      {/*</section>*/}

      <section className="py-16 px-6 md:px-10 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 p-4 bg-muted/50 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground italic text-center">
              All projects shown in this portfolio were built by our team for skill showcase and are not commissioned work. Due to NDAs and client confidentiality, we do not display actual client projects publicly.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-10 lg:px-20 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Let's discuss how we can help you achieve similar results
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" onClick={() => navigate('/contact')}>
              Get in Touch
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/portfolio')}>
              View More Projects
            </Button>
          </div>
        </div>
      </section>

      {selectedImage && (
        <ImageViewer 
          src={selectedImage.src} 
          alt={selectedImage.alt} 
          onClose={() => setSelectedImage(null)} 
        />
      )}

      <Footer />
    </div>
  );
};

export default ProjectDetails;